/**
 * The journey ladder above the timeline: Information -> ... -> Payout.
 *
 * It answers the question the raw node labels could not — how far this
 * relationship actually got — and doubles as the filter, so clicking
 * "Conversion" cuts the timeline down to admissions.
 *
 * Stages this journey never reached stay visible but dimmed. That absence is
 * the finding on a client who is visited constantly and never converts, and it
 * disappears if unreached stages are simply omitted.
 */
import { ChevronRight } from "lucide-react";

import { STAGES, type Stage } from "@/components/timeline/stages";

export interface StageLadderProps {
  counts: Map<number, number>;
  hidden: Set<number>;
  onToggle: (n: number) => void;
  onReset: () => void;
}

export function StageLadder({ counts, hidden, onToggle, onReset }: StageLadderProps) {
  const reached = STAGES.filter((s) => (counts.get(s.n) ?? 0) > 0);
  const furthest = reached.length ? reached[reached.length - 1]! : null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="label">journey stage</span>
        {furthest ? (
          <span className="text-[12px] text-ink-muted">
            reached <strong style={{ color: furthest.color }}>{furthest.name}</strong>
            <span className="text-ink-faint"> · {furthest.meaning.toLowerCase()}</span>
          </span>
        ) : (
          <span className="text-[12px] text-ink-faint">no stage recorded</span>
        )}
        {hidden.size > 0 && (
          <button onClick={onReset} className="text-[11.5px] text-petrol underline ml-auto">
            show all stages
          </button>
        )}
      </div>

      <div className="flex items-stretch gap-0.5 overflow-x-auto pb-1">
        {STAGES.map((stage, i) => (
          <StageStep
            key={stage.n}
            stage={stage}
            count={counts.get(stage.n) ?? 0}
            off={hidden.has(stage.n)}
            last={i === STAGES.length - 1}
            onToggle={() => onToggle(stage.n)}
          />
        ))}
      </div>
    </div>
  );
}

function StageStep({
  stage, count, off, last, onToggle,
}: {
  stage: Stage; count: number; off: boolean; last: boolean; onToggle: () => void;
}) {
  const empty = count === 0;
  return (
    <div className="flex items-stretch shrink-0">
      <button
        onClick={onToggle}
        disabled={empty}
        title={empty
          ? `${stage.name} — ${stage.meaning}. Not reached in this journey.`
          : `${stage.name} — ${stage.meaning}. Click to ${off ? "show" : "hide"}.`}
        className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left
                    transition-opacity ${empty ? "opacity-40 cursor-default" : ""}
                    ${off ? "opacity-40" : ""}
                    focus-visible:outline focus-visible:outline-2 focus-visible:outline-petrol`}
        style={{
          borderColor: empty ? "var(--rule, #E4E2DD)" : `${stage.color}44`,
          background: empty || off ? "transparent" : stage.bg,
        }}
      >
        <span
          className="w-6 h-6 rounded-full grid place-items-center shrink-0"
          style={{
            background: empty ? "transparent" : stage.color,
            border: empty ? "1.5px dashed #B9B5AC" : "none",
          }}
        >
          <stage.Icon size={12} style={{ color: empty ? "#B9B5AC" : "#fff" }} />
        </span>
        <span className="min-w-0">
          <span className="flex items-baseline gap-1.5">
            <span className="font-display font-semibold text-[12.5px] whitespace-nowrap"
                  style={{ color: empty ? undefined : stage.color }}>
              {stage.n}. {stage.name}
            </span>
          </span>
          <span className="block text-[11px] tnum text-ink-faint">
            {empty ? "not reached" : `${count.toLocaleString("en-IN")} event${count === 1 ? "" : "s"}`}
          </span>
        </span>
      </button>
      {!last && (
        <ChevronRight size={13} className="self-center mx-0.5 shrink-0 text-ink-faint" />
      )}
    </div>
  );
}
